/**
 * GTM dataLayer helpers for GA4 + Google Ads + Meta + Pinterest.
 *
 * Everything goes through window.dataLayer.push() so GTM handles
 * tag firing, consent gating, and platform routing.
 *
 * GTM Container: GTM-T8PVRK33
 * GA4: G-QGRHD2X1KG
 * Google Ads: AW-18059753112 (Purchase label: mE4lCNfGxpQcEJjtx6ND)
 * Meta Pixel: 2106078823286329
 * Pinterest Tag: 2612440648567
 */

import { isTrackingEnabled } from "@/lib/trackingEnvironment";
import { isPurchaseTrackingPaused } from "@/hooks/usePurchaseKillSwitch";
import { recordKillSwitchTelemetry } from "@/lib/purchaseKillSwitchTelemetry";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const isDebug = typeof window !== 'undefined' && isTrackingEnabled() && new URLSearchParams(window.location.search).has('gtm_debug');

// ─── PURCHASE DENYLIST ──────────────────────────────────────
// Canonical transaction_id format: "zooi-{shopify_order_id}"
// Any purchase-related event without this prefix is blocked at the
// dataLayer level so GTM/GA4/Ads can never double-count.

const CANONICAL_TX_PREFIX = 'zooi-';

/** Events that require canonical transaction_id validation */
const PURCHASE_EVENT_DENYLIST = new Set([
  'purchase',
  'purchase_client_hint',
  'refund',
]);

/** Validate that a purchase-related event carries a canonical transaction_id */
function isCanonicalTransactionId(txId: unknown): boolean {
  return typeof txId === 'string' && txId.startsWith(CANONICAL_TX_PREFIX) && txId.length > CANONICAL_TX_PREFIX.length;
}

function extractTransactionId(data: Record<string, unknown>): unknown {
  // Check direct, ecommerce.transaction_id, and event_id
  return data.transaction_id
    ?? (data.ecommerce as Record<string, unknown> | undefined)?.transaction_id
    ?? data.event_id;
}

function shouldBlockPurchaseEvent(eventName: string, data: Record<string, unknown>): string | null {
  if (!PURCHASE_EVENT_DENYLIST.has(eventName)) return null;

  const txId = extractTransactionId(data);

  if (!txId) {
    return `missing transaction_id`;
  }
  if (!isCanonicalTransactionId(txId)) {
    return `non-canonical transaction_id "${txId}" (expected ${CANONICAL_TX_PREFIX}*)`;
  }
  return null;
}

function pushEvent(eventName: string, data: Record<string, unknown> = {}) {
  if (typeof window === 'undefined' || !isTrackingEnabled()) return;
  window.dataLayer = window.dataLayer || [];

  // ── Denylist check: block non-canonical purchase events ──
  const blockReason = shouldBlockPurchaseEvent(eventName, data);
  if (blockReason) {
    console.warn(
      `%c[dataLayer] BLOCKED ${eventName}: ${blockReason}`,
      'color:#ef4444;font-weight:bold',
      { eventName, data }
    );
    return;
  }

  // Clear previous ecommerce object to avoid stale data
  if (data.ecommerce !== undefined) {
    window.dataLayer.push({ ecommerce: null });
  }

  window.dataLayer.push({ event: eventName, ...data });

  if (isDebug) {
    console.log(`%c[dataLayer] ${eventName}`, 'color:#16a34a;font-weight:bold', data);
  }
}

/**
 * Install a dataLayer proxy that intercepts ALL pushes (including from
 * external scripts, GTM custom HTML tags, or injected code) and blocks
 * any purchase-related event that doesn't carry a canonical zooi- tx ID.
 *
 * Call once during app bootstrap (gtmBootstrap).
 */
export function installDataLayerDenylist() {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];

  const originalPush = window.dataLayer.push.bind(window.dataLayer);

  window.dataLayer.push = function (...items: unknown[]) {
    const filtered = items.filter((item) => {
      if (!item || typeof item !== 'object' || !('event' in (item as Record<string, unknown>))) {
        return true; // pass through non-event pushes (gtag commands, ecommerce clears, etc.)
      }
      const obj = item as Record<string, unknown>;
      const eventName = String(obj.event || '');
      const blockReason = shouldBlockPurchaseEvent(eventName, obj);
      if (blockReason) {
        console.warn(
          `%c[dataLayer proxy] BLOCKED external ${eventName}: ${blockReason}`,
          'color:#ef4444;font-weight:bold;background:#fef2f2;padding:2px 6px;border-radius:3px',
          obj
        );
        return false;
      }
      return true;
    });

    if (filtered.length === 0) return filtered.length;
    return originalPush(...filtered);
  } as typeof Array.prototype.push;
}

// ─── PURCHASE KILL-SWITCH ───────────────────────────────────
let _purchaseKillSwitchLoaded = false;
let _purchaseKillSwitchActive = false;

/** Pre-load the kill-switch flag so purchase functions can check it synchronously. */
export async function preloadPurchaseKillSwitch(): Promise<void> {
  try {
    _purchaseKillSwitchActive = await isPurchaseTrackingPaused();
  } catch {
    _purchaseKillSwitchActive = false;
  }
  _purchaseKillSwitchLoaded = true;
}

function isPurchaseKilled(): boolean {
  if (!_purchaseKillSwitchLoaded) {
    // Fire-and-forget preload for next call
    preloadPurchaseKillSwitch();
    return false; // fail-open on first call
  }
  return _purchaseKillSwitchActive;
}

// ─── PURCHASE DEDUPLICATION ─────────────────────────────────
const SENT_PURCHASES_KEY = 'zooi_sent_purchases';
const PENDING_PURCHASE_KEY = 'zooi_pending_purchase';

function getSentPurchases(): string[] {
  try { return JSON.parse(localStorage.getItem(SENT_PURCHASES_KEY) || '[]'); }
  catch { return []; }
}

function markPurchaseSent(transactionId: string) {
  const sent = getSentPurchases();
  sent.push(transactionId);
  if (sent.length > 100) sent.splice(0, sent.length - 100);
  localStorage.setItem(SENT_PURCHASES_KEY, JSON.stringify(sent));
}

function isPurchaseAlreadySent(transactionId: string): boolean {
  return getSentPurchases().includes(transactionId);
}

// ─── TYPES ──────────────────────────────────────────────────

export interface GtmItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_variant?: string;
  item_list_name?: string;
  item_list_id?: string;
  discount?: number;
  currency?: string;
  index?: number;
}

// ─── USER DATA ───────────────────────────────────────────────

/** Push user_data for enhanced conversions (GA4 + Google Ads) */
export function gtmSetUserData(data: {
  email?: string; phone?: string; firstName?: string; lastName?: string; userId?: string;
}) {
  pushEvent('set_user_data', {
    user_data: {
      email: data.email?.toLowerCase().trim(),
      phone_number: data.phone,
      address: { first_name: data.firstName, last_name: data.lastName },
    },
    user_id: data.userId,
  });
}

// ─── ECOMMERCE EVENTS (GA4 standard) ───────────────────────

export function gtmViewItemList(items: GtmItem[], listName: string, listId?: string) {
  pushEvent('view_item_list', {
    ecommerce: {
      item_list_name: listName,
      item_list_id: listId || listName.toLowerCase().replace(/\s/g, '_'),
      items: items.map((item, i) => ({ ...item, index: item.index ?? i, currency: item.currency || 'SEK' })),
    },
  });
}

export function gtmSelectItem(item: GtmItem, listName: string, listId?: string) {
  pushEvent('select_item', {
    ecommerce: {
      item_list_name: listName,
      item_list_id: listId || listName.toLowerCase().replace(/\s/g, '_'),
      items: [{ ...item, currency: item.currency || 'SEK' }],
    },
  });
}

// ─── view_item dedupe ──────────────────────────────────────
// Goal: exactly ONE view_item per product per session.
// Defends against: React StrictMode double-mount, effect re-runs, SPA
// back/forward navigation, GTM tag mis-config, and any caller that fires
// from outside a PDP route.
const VIEW_ITEM_DEDUPE_KEY = 'zooi_view_item_dedupe';
// Per-product: full session (sessionStorage already scopes to tab/session).
// Use a long TTL (24h) just to bound the map size.
const VIEW_ITEM_DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000;

// In-memory guard: blocks two synchronous fires within the same tick
// (React StrictMode mounts effects twice in dev; this catches it before
// sessionStorage even sees the second call).
const viewItemFiredThisLoad = new Set<string>();

function isOnProductDetailRoute(): boolean {
  if (typeof window === 'undefined') return false;
  // Strict allowlist: only Swedish PDP route. Defense-in-depth.
  return /^\/produkt\/[^/]+\/?$/.test(window.location.pathname);
}

function shouldFireViewItem(itemId: string): string | null {
  if (typeof window === 'undefined') return null;

  // Guard 1: Route check — never fire outside PDP.
  if (!isOnProductDetailRoute()) {
    if (isDebug) console.log(`%c[dataLayer] view_item BLOCKED (not on PDP: ${window.location.pathname})`, 'color:#dc2626;font-weight:bold');
    return null;
  }

  // Guard 2: In-memory (catches StrictMode double-mount synchronously).
  if (viewItemFiredThisLoad.has(itemId)) {
    if (isDebug) console.log(`%c[dataLayer] view_item SKIPPED (in-memory: ${itemId})`, 'color:#f59e0b;font-weight:bold');
    return null;
  }

  // Guard 3: sessionStorage (survives SPA navigation within same tab).
  try {
    const raw = sessionStorage.getItem(VIEW_ITEM_DEDUPE_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    for (const k of Object.keys(map)) {
      if (now - map[k] > VIEW_ITEM_DEDUPE_WINDOW_MS) delete map[k];
    }
    if (map[itemId] && now - map[itemId] < VIEW_ITEM_DEDUPE_WINDOW_MS) {
      viewItemFiredThisLoad.add(itemId);
      if (isDebug) console.log(`%c[dataLayer] view_item SKIPPED (session: ${itemId})`, 'color:#f59e0b;font-weight:bold');
      return null;
    }
    map[itemId] = now;
    sessionStorage.setItem(VIEW_ITEM_DEDUPE_KEY, JSON.stringify(map));
    viewItemFiredThisLoad.add(itemId);
    // Stable event_id: itemId + session bucket → GA4 server-side dedupe too.
    return `vi_${itemId}_${Math.floor(now / VIEW_ITEM_DEDUPE_WINDOW_MS)}`;
  } catch {
    viewItemFiredThisLoad.add(itemId);
    return `vi_${itemId}_${Date.now()}`;
  }
}

export function gtmViewItem(item: GtmItem) {
  const eventId = shouldFireViewItem(item.item_id);
  if (!eventId) return;

  pushEvent('view_item', {
    event_id: eventId,
    ecommerce: {
      currency: item.currency || 'SEK',
      value: item.price * item.quantity,
      items: [{ ...item, currency: item.currency || 'SEK' }],
    },
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: 'product',
    num_items: item.quantity,
  });
}

export function gtmAddToCart(item: GtmItem) {
  pushEvent('add_to_cart', {
    ecommerce: {
      currency: item.currency || 'SEK',
      value: item.price * item.quantity,
      items: [{ ...item, currency: item.currency || 'SEK' }],
    },
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: 'product',
    num_items: item.quantity,
  });
}

export function gtmRemoveFromCart(item: GtmItem) {
  pushEvent('remove_from_cart', {
    ecommerce: {
      currency: item.currency || 'SEK',
      value: item.price * item.quantity,
      items: [{ ...item, currency: item.currency || 'SEK' }],
    },
  });
}

export function gtmViewCart(items: GtmItem[], totalValue: number) {
  pushEvent('view_cart', {
    ecommerce: {
      currency: 'SEK',
      value: totalValue,
      items: items.map((item, i) => ({ ...item, index: i, currency: 'SEK' })),
    },
  });
}

export function gtmBeginCheckout(items: GtmItem[], totalValue: number, coupon?: string) {
  pushEvent('begin_checkout', {
    ecommerce: {
      currency: 'SEK',
      value: totalValue,
      coupon,
      items: items.map((item, i) => ({ ...item, index: i, currency: 'SEK' })),
    },
  });
}

export function gtmAddShippingInfo(items: GtmItem[], totalValue: number, shippingTier: string, coupon?: string) {
  pushEvent('add_shipping_info', {
    ecommerce: {
      currency: 'SEK',
      value: totalValue,
      shipping_tier: shippingTier,
      coupon,
      items: items.map((item, i) => ({ ...item, index: i, currency: 'SEK' })),
    },
  });
}

export function gtmAddPaymentInfo(items: GtmItem[], totalValue: number, paymentType: string, coupon?: string) {
  pushEvent('add_payment_info', {
    ecommerce: {
      currency: 'SEK',
      value: totalValue,
      payment_type: paymentType,
      coupon,
      items: items.map((item, i) => ({ ...item, index: i, currency: 'SEK' })),
    },
  });
}

/**
 * @deprecated — Server-side Measurement Protocol is the canonical purchase source.
 * Browser-side purchase was removed to prevent double-counting in GA4.
 * Use gtmPurchaseClientHint() instead (fires purchase_client_hint, not purchase).
 */
export function gtmPurchase(
  transactionId: string,
  items: GtmItem[],
  totalValue: number,
  shipping = 0,
  tax = 0,
  coupon?: string
) {
  // Redirect to purchase_client_hint to avoid triggering GA4 purchase tag in GTM
  gtmPurchaseClientHint(transactionId, items, totalValue, shipping, tax, coupon);
}

export function retryPendingPurchase() {
  try {
    const pending = localStorage.getItem(PENDING_PURCHASE_KEY);
    if (!pending) return;
    const data = JSON.parse(pending);
    if (Date.now() - data.savedAt > 86400000) {
      localStorage.removeItem(PENDING_PURCHASE_KEY);
      return;
    }
    // Use client hint instead of purchase to avoid GA4 double-count
    gtmPurchaseClientHint(data.transactionId, data.items, data.totalValue, data.shipping, data.tax, data.coupon);
  } catch { localStorage.removeItem(PENDING_PURCHASE_KEY); }
}

/**
 * Browser-side purchase HINT — NOT a parallel purchase event.
 *
 * Server-side webhook (`track-purchase-server-side`) is the canonical source of
 * truth for GA4 + Google Ads purchase conversions. This event exists only so
 * GTM can fire browser-side Meta Pixel / Pinterest Tag (which dedupe via event_id)
 * and so we have UX/debug telemetry on the confirmation page itself.
 *
 * GA4 Purchase tag in GTM must NOT trigger on this event.
 */
export function gtmPurchaseClientHint(
  transactionId: string,
  items: GtmItem[],
  totalValue: number,
  shipping = 0,
  tax = 0,
  coupon?: string,
) {
  if (isPurchaseKilled()) {
    if (isDebug) console.log(`%c[dataLayer] purchase_client_hint BLOCKED (kill-switch active)`, 'color:#ef4444;font-weight:bold');
    recordKillSwitchTelemetry({
      event_type: "gate_blocked",
      paused_state: true,
      extra: { transaction_id: transactionId, value: totalValue, items: items.length },
    });
    return;
  }
  if (isPurchaseAlreadySent(transactionId)) {
    if (isDebug) console.log(`%c[dataLayer] purchase_client_hint SKIPPED (duplicate: ${transactionId})`, 'color:#f59e0b;font-weight:bold');
    return;
  }
  pushEvent('purchase_client_hint', {
    event_id: transactionId,
    ecommerce: {
      transaction_id: transactionId,
      currency: 'SEK',
      value: totalValue,
      shipping,
      tax,
      coupon,
      items: items.map((item, i) => ({ ...item, index: i, currency: 'SEK' })),
    },
    content_ids: items.map(i => i.item_id),
    content_type: 'product',
    num_items: items.reduce((sum, i) => sum + i.quantity, 0),
  });
  markPurchaseSent(transactionId);
  localStorage.removeItem(PENDING_PURCHASE_KEY);
  recordKillSwitchTelemetry({
    event_type: "gate_allowed",
    paused_state: false,
    extra: { transaction_id: transactionId, value: totalValue, items: items.length },
  });
}

// ─── ENGAGEMENT EVENTS ──────────────────────────────────────

export function gtmSearch(searchTerm: string, resultsCount?: number) {
  pushEvent('search', { search_term: searchTerm, results_count: resultsCount });
}

export function gtmLogin(method: string) {
  pushEvent('login', { method });
}

export function gtmSignUp(method: string) {
  pushEvent('sign_up', { method });
}

export function gtmShare(contentType: string, itemId: string) {
  pushEvent('share', { content_type: contentType, item_id: itemId });
}

export function gtmNewsletterSignup() {
  pushEvent('generate_lead', { currency: 'SEK', value: 0, lead_source: 'newsletter' });
}

/**
 * Generic GA4 generate_lead helper for forms (contact, support, callbacks).
 * All lead pushes MUST go through here so the dataLayer denylist proxy and
 * isTrackingEnabled gate are applied uniformly.
 */
export function gtmGenerateLead(params: {
  leadSource: string;
  formLocation?: string;
  formSubject?: string;
  value?: number;
  currency?: string;
}) {
  pushEvent('generate_lead', {
    lead_source: params.leadSource,
    form_location: params.formLocation,
    form_subject: params.formSubject || undefined,
    currency: params.currency || 'SEK',
    value: params.value ?? 0,
  });
}

export function gtmWishlistAdd(item: GtmItem) {
  pushEvent('add_to_wishlist', {
    ecommerce: {
      currency: item.currency || 'SEK',
      value: item.price,
      items: [{ ...item, currency: item.currency || 'SEK' }],
    },
  });
}

export function gtmException(description: string, fatal = false) {
  pushEvent('exception', { description, fatal });
}

/** Detect browser family for checkout analytics */
function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (/edg\//i.test(ua)) return 'edge';
  if (/opr\//i.test(ua) || /opera/i.test(ua)) return 'opera';
  if (/firefox\//i.test(ua)) return 'firefox';
  if (/crios/i.test(ua)) return 'chrome_ios';
  if (/chrome\//i.test(ua) && !/edg/i.test(ua)) return 'chrome';
  if (/^((?!chrome|android).)*safari/i.test(ua)) return 'safari';
  return 'other';
}

/**
 * Log checkout navigation attempt for browser compatibility analysis.
 * Fires a custom GTM event so we can build reports on which browsers
 * still cause issues with the checkout redirect flow.
 */
export function gtmCheckoutNavigation(params: {
  method: 'redirect' | 'fallback_banner' | 'retry_same_tab';
  browser: string;
  success: boolean;
  error_reason?: string;
  has_checkout_url: boolean;
  cart_value?: number;
  item_count?: number;
}) {
  pushEvent('checkout_navigation', {
    nav_method: params.method,
    nav_browser: params.browser,
    nav_success: params.success,
    nav_error_reason: params.error_reason || null,
    nav_has_checkout_url: params.has_checkout_url,
    nav_cart_value: params.cart_value,
    nav_item_count: params.item_count,
    nav_is_mobile: /mobile|iphone|ipad|android/i.test(navigator.userAgent),
    nav_user_agent: navigator.userAgent.slice(0, 200),
  });
}

// ─── POST-PURCHASE LANDING TELEMETRY ───────────────────────
/**
 * Fires `post_purchase_landing` to GA4 (via dataLayer) so vi i Looker/GA4
 * kan se om kunder efter checkout faktiskt landar på rätt domän
 * (`zooi.se`) och om UTM/sessionssignaler överlevde checkout-redirecten.
 *
 * Skicka snapshot av attribution-data INNAN clearSessionAttribution() körs.
 */
export function gtmPostPurchaseLanding(snapshot: {
  zooi_session_id?: string | null;
  stored_gclid?: string | null;
  stored_utm_source?: string | null;
  stored_utm_medium?: string | null;
  stored_utm_campaign?: string | null;
  landing_page?: string | null;
}) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const expectedHost = 'zooi.se';
  const host = url.hostname;
  const isCanonical = host === expectedHost || host === `www.${expectedHost}`;
  const referrer = document.referrer || '';
  let referrerHost = '';
  try { referrerHost = referrer ? new URL(referrer).hostname : ''; } catch { /* ignore */ }
  const referrerIsShopify = /\.myshopify\.com$/i.test(referrerHost) || /shopify\.com$/i.test(referrerHost);
  const referrerIsCanonical = referrerHost === expectedHost || referrerHost === `www.${expectedHost}`;

  const params = url.searchParams;
  const urlGclid = params.get('gclid');
  const urlUtmSource = params.get('utm_source');

  // GA4 client_id + session_id (from _ga / _ga_<measurement> cookie)
  let gaClientId: string | null = null;
  let gaSessionId: string | null = null;
  try {
    const cookies = document.cookie.split('; ');
    const ga = cookies.find(c => c.startsWith('_ga='));
    if (ga) {
      const v = ga.split('=')[1] || '';
      const parts = v.split('.');
      if (parts.length >= 4) gaClientId = `${parts[2]}.${parts[3]}`;
    }
    const gaSession = cookies.find(c => /^_ga_[A-Z0-9]+=/i.test(c));
    if (gaSession) {
      const v = gaSession.split('=')[1] || '';
      // Format: GS1.1.<sessionId>.<sessionNumber>...
      const parts = v.split('.');
      if (parts.length >= 3) gaSessionId = parts[2];
    }
  } catch { /* cookies blocked */ }

  pushEvent('post_purchase_landing', {
    pp_target_host: host,
    pp_target_path: url.pathname,
    pp_expected_host: expectedHost,
    pp_is_canonical_domain: isCanonical,
    pp_protocol: url.protocol,
    pp_has_referrer: !!referrer,
    pp_referrer_host: referrerHost || null,
    pp_referrer_is_shopify: referrerIsShopify,
    pp_referrer_is_canonical: referrerIsCanonical,
    pp_url_has_gclid: !!urlGclid,
    pp_url_has_utm: !!urlUtmSource,
    pp_stored_has_gclid: !!snapshot.stored_gclid,
    pp_stored_utm_source: snapshot.stored_utm_source || null,
    pp_stored_utm_medium: snapshot.stored_utm_medium || null,
    pp_stored_utm_campaign: snapshot.stored_utm_campaign || null,
    pp_zooi_session_id: snapshot.zooi_session_id || null,
    pp_session_continuity_ok:
      isCanonical && !!snapshot.zooi_session_id && (!!snapshot.stored_utm_source || !!snapshot.stored_gclid || referrerIsCanonical),
    pp_ga_client_id: gaClientId,
    pp_ga_session_id: gaSessionId,
    pp_landing_page: snapshot.landing_page || null,
    pp_user_agent: navigator.userAgent.slice(0, 200),
  });
}

/** Convenience: get browser string for external callers */
export { detectBrowser };

// ─── BACKWARD COMPAT (no-ops, GTM handles routing) ─────────

export function gadsConversion(_transactionId: string, _value: number, _currency = 'SEK') {
  // Handled by GTM via purchase event
}

export function gadsViewItem(_item: { id: string; name: string; price: number; category?: string }) {
  // Handled by GTM via view_item event
}

export function gadsAddToCart(_item: { id: string; price: number }) {
  // Handled by GTM via add_to_cart event
}

export function gadsPurchase(_transactionId: string, _items: { id: string; price: number }[], _value: number) {
  // Handled by GTM via purchase event
}

/**
 * Fired on SPA virtual page view (route change AFTER initial load).
 *
 * NEVER fired on first load — GA4 Config Tag in GTM owns the initial
 * page_view (send_page_view: true). See ScrollToTop.tsx for the gating.
 *
 * Strategy: fire BOTH event names so the setup works without any GTM
 * console changes:
 *   - `page_view`     → consumed by existing GTM trigger (legacy SPA pageview tag)
 *   - `spa_page_view` → consumed by future dedicated GA4 Event Tag (when added)
 *
 * Each push carries the SAME `event_id` (UUID per route-change). GA4 ignores
 * duplicate events with identical `event_id` within a 60-minute window, so
 * even if both events fire and both reach GA4, GA4 records only ONE
 * page_view per navigation.
 *
 * To remove the legacy `page_view` push later: delete the first pushEvent()
 * call and add the SPA Page View tag in GTM (see Definition of Done).
 */
export function gtmPageView(path: string, title: string) {
  // Generate a single event_id shared between both pushes for GA4 dedup
  const eventId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `pv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const params = {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title,
    spa_navigation: true,
    event_id: eventId,
  };

  // Legacy: fires existing GTM SPA page_view trigger (no GTM change required)
  pushEvent('page_view', params);

  // New: fires future dedicated SPA Page View tag (when GTM is updated)
  // GA4 will dedup against the legacy push via shared event_id.
  pushEvent('spa_page_view', params);
}

